import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private _apiUri: string | null = null;
  private _clientId: string | null = null;
  private _clientSecret: string | null = null;
  private _isSetupDone: boolean =
    localStorage.getItem('isSetupDone') === 'true' || false;
  private _endpointGuid: string | null = null;
  private _hostname: string = localStorage.getItem('hostname') || 'NEW DEVICE';
  private _resetCode: string = localStorage.getItem('resetCode') || '21539213';
  private _isRegistered: boolean =
    localStorage.getItem('isRegistered') === 'true' || false;
  private _bearerToken: string | null = null;
  private _refreshToken: string | null = null;
  private _deviceId: number | null = null;
  private _companyName$: BehaviorSubject<string> = new BehaviorSubject<string>(
    localStorage.getItem('companyName') || ''
  );
  private _companyLogo$: BehaviorSubject<string> = new BehaviorSubject<string>(
    localStorage.getItem('companyLogo') || ''
  );
  private _numpadEnabled$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(
      localStorage.getItem('numpadEnabled') === 'true'
    );
  private _updateInterval: number = localStorage.getItem('updateInterval') 
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    ? parseInt(localStorage.getItem('updateInterval')!, 10) 
    : 60 * 60 * 1000;

  constructor(private http: HttpClient) {}

  public set apiUri(baseUri: string | null) {
    this._apiUri = baseUri;
    if (baseUri !== null) {
      localStorage.setItem('apiUri', baseUri);
    }
  }

  public get apiUri(): string | null {
    if (this._apiUri === null) {
      this._apiUri = localStorage.getItem('apiUri');
    }
    return this._apiUri;
  }

  public set clientId(key: string | null) {
    this._clientId = key;
    if (key !== null) {
      localStorage.setItem('clientId', key);
    }
  }

  public get clientId(): string | null {
    if (this._clientId === null) {
      this._clientId = localStorage.getItem('clientId');
    }
    return this._clientId;
  }

  public set clientSecret(secret: string | null) {
    this._clientSecret = secret;
    if (secret !== null) {
      localStorage.setItem('clientSecret', secret);
    }
  }

  public get clientSecret(): string | null {
    if (this._clientSecret === null) {
      this._clientSecret = localStorage.getItem('clientSecret');
    }
    return this._clientSecret;
  }

  public set isSetupDone(isSetupDone: boolean) {
    this._isSetupDone = isSetupDone;
    localStorage.setItem('isSetupDone', isSetupDone.toString());
  }

  public get isSetupDone(): boolean {
    return this._isSetupDone;
  }

  public set hostname(hostname: string) {
    this._hostname = hostname;
    if (hostname !== null) {
      localStorage.setItem('hostname', hostname.toString());
    }
  }

  public get hostname(): string {
    return this._hostname;
  }

  public get updateInterval(): number {
    return this._updateInterval;
  }

  public set resetCode(resetCode: string) {
    this._resetCode = resetCode;
    if (resetCode !== null) {
      localStorage.setItem('resetCode', resetCode.toString());
    }
  }

  public get resetCode(): string {
    return this._resetCode;
  }

  public set isRegistered(isRegistered: boolean) {
    this._isRegistered = isRegistered;
    localStorage.setItem('isRegistered', isRegistered.toString());
  }

  public get isRegistered(): boolean {
    return this._isRegistered;
  }

  public set bearerToken(bearerToken: string | null) {
    this._bearerToken = bearerToken;
  }

  public get bearerToken(): string | null {
    return this._bearerToken;
  }

  public set refreshToken(refreshToken: string | null) {
    this._refreshToken = refreshToken;
    if (refreshToken !== null) {
      localStorage.setItem('refreshToken', refreshToken);
    } else {
      localStorage.removeItem('refreshToken');
    }
  }

  public get refreshToken(): string | null {
    if (this._refreshToken === null) {
      this._refreshToken = localStorage.getItem('refreshToken');
    }
    return this._refreshToken;
  }

  public set deviceId(deviceId: number | null) {
    this._deviceId = deviceId;
    if (deviceId !== null) {
      localStorage.setItem('deviceId', `${deviceId}`);
    }
  }

  public get deviceId(): number | null {
    if (this._deviceId === null) {
      this._deviceId = parseInt(localStorage.getItem('deviceId') || '-1', 10);
    }
    return this._deviceId;
  }

  public setCompanyName(companyName: string) {
    this._companyName$.next(companyName);
    localStorage.setItem('companyName', companyName);
  }

  public get companyName(): Observable<string> {
    return this._companyName$.asObservable();
  }

  public setCompanyLogo(companyLogo: string) {
    this._companyLogo$.next(companyLogo);
    localStorage.setItem('companyLogo', companyLogo);
  }

  public get companyLogo(): Observable<string> {
    return this._companyLogo$.asObservable();
  }

  public setNumpadEnabled(numpadEnabled: boolean) {
    this._numpadEnabled$.next(numpadEnabled);
    localStorage.setItem('numpadEnabled', numpadEnabled ? 'true' : 'false');
  }

  public get numpadEnabled(): Observable<boolean> {
    return this._numpadEnabled$.asObservable();
  }

  public get endpointGuid(): string {
    if (this._endpointGuid === null) {
      this._endpointGuid = this.generateGuid();
      localStorage.setItem('endpointGuid', this._endpointGuid);
    }
    return this._endpointGuid;
  }

  generateGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0,
          v = c == 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  }
}
